import React, {
	createContext,
	useContext,
	// useEffect,
	useState
} from 'react';
import {
	// fetchAuthSession,
	signIn,
	signOut,
	signUp,
	confirmSignIn,
	confirmSignUp,
	resendSignUpCode,
	resetPassword,
	confirmResetPassword,
	getCurrentUser
	// updatePassword,
	// updateUserAttributes,
	// deleteUser
} from '@aws-amplify/auth';
import PropTypes from 'prop-types';
import { CHALLENGE_NAMES } from '../helpers/enums';
// import { getRoute, ROUTE_ENUMS } from '../helpers/routes';
// import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

export const AuthProvider = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [user, setUser] = useState(null);
	// const [username, setUsername] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [authStep, setAuthStep] = useState(null); // For handling MFA flows

	// Function to check auth status with proper loading handling
	const checkAuthStatus = async (bypassCache = true) => {
		try {
			setLoading(true);
			setError(null);

			// For external calls, we want to bypass cache
			// const session = await fetchAuthSession({ bypassCache });

			// Check if user is authenticated by trying to get current user
			const currentUser = await getCurrentUser();

			if (currentUser) {
				setIsAuthenticated(true);
				setUser(currentUser);
				setAuthStep(null);
			} else {
				setIsAuthenticated(false);
				setUser(null);
				setAuthStep(null);
			}
		} catch (err) {
			setIsAuthenticated(false);
			setUser(null);
			setAuthStep(null);
			if (err !== 'not authenticated' && err.message !== 'not authenticated') {
				setError(err);
			}
		} finally {
			setLoading(false);
		}
	};

	const signInUser = async ({ username, password }) => {
		try {
			setLoading(true);
			const result = await signIn({ username, password });
			console.log('Sign In Result', result);

			// Handle MFA flow
			if (
				result.nextStep &&
				result.nextStep.signInStep ===
					CHALLENGE_NAMES.CONFIRM_SIGN_IN_WITH_TOTP_CODE
			) {
				setIsAuthenticated(false);
				// setUsername(username);
				setUser(result.user);
				setAuthStep(CHALLENGE_NAMES.CONFIRM_SIGN_IN_WITH_TOTP_CODE);
				// navigate(getRoute(ROUTE_ENUMS.MFA), { replace: true });
				// return {
				// 	success: true,
				// 	user: result.user,
				// 	nextStep: result.nextStep
				// };
			}

			// Regular sign in success
			setIsAuthenticated(true);
			setUser(result.user);
			setLoading(false);
			return { success: true, user: result.user };
		} catch (err) {
			setError(err);
			return { success: false, error: err };
		}
	};

	const confirmSignInWithTOTP = async ({ mfaCode }) => {
		try {
			const result = await confirmSignIn({ challengeResponse: mfaCode }); // This might not be correct

			setIsAuthenticated(true);
			setUser(result.user);
			setAuthStep(null);
			return { success: true };
		} catch (err) {
			setError(err);
			return { success: false, error: err };
		}
	};

	const signOutUser = async () => {
		try {
			await signOut();
			setIsAuthenticated(false);
			setUser(null);
			setError(null);
			setAuthStep(null);
		} catch (err) {
			setError(err);
		}
	};

	const signUpUser = async (username, password, email) => {
		try {
			const result = await signUp({
				username,
				password,
				options: {
					userAttributes: { email }
				}
			});
			return { success: true, user: result.user };
		} catch (err) {
			setError(err);
			return { success: false, error: err };
		}
	};

	const confirmSignUpUser = async (username, code) => {
		try {
			await confirmSignUp({ username, confirmationCode: code });
			return { success: true };
		} catch (err) {
			setError(err);
			return { success: false, error: err };
		}
	};

	const resendConfirmationCode = async (username) => {
		try {
			await resendSignUpCode({ username });
			return { success: true };
		} catch (err) {
			setError(err);
			return { success: false, error: err };
		}
	};

	const forgotPasswordUser = async (username) => {
		try {
			await resetPassword({ username });
			return { success: true };
		} catch (err) {
			setError(err);
			return { success: false, error: err };
		}
	};

	const forgotPasswordSubmitUser = async (username, code, newPassword) => {
		try {
			await confirmResetPassword({
				username,
				confirmationCode: code,
				newPassword
			});
			return { success: true };
		} catch (err) {
			setError(err);
			return { success: false, error: err };
		}
	};

	const value = {
		isAuthenticated,
		user,
		isLoading: loading,
		error,
		authStep, // Add this to expose current auth step
		signIn: signInUser,
		signOut: signOutUser,
		signUp: signUpUser,
		confirmSignUp: confirmSignUpUser,
		resendConfirmationCode,
		forgotPassword: forgotPasswordUser,
		forgotPasswordSubmit: forgotPasswordSubmitUser,
		checkAuthStatus,
		confirmSignInWithTOTP // Add this for MFA confirmation
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
	children: PropTypes.node.isRequired
};

// Add PropTypes for the context value
// const AuthContextType = PropTypes.shape({
// 	isAuthenticated: PropTypes.bool.isRequired,
// 	user: PropTypes.object,
// 	loading: PropTypes.bool.isRequired,
// 	error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
// 	authStep: PropTypes.string,
// 	signIn: PropTypes.func.isRequired,
// 	signOut: PropTypes.func.isRequired,
// 	signUp: PropTypes.func.isRequired,
// 	confirmSignUp: PropTypes.func.isRequired,
// 	resendConfirmationCode: PropTypes.func.isRequired,
// 	forgotPassword: PropTypes.func.isRequired,
// 	forgotPasswordSubmit: PropTypes.func.isRequired,
// 	checkAuthStatus: PropTypes.func.isRequired,
// 	confirmSignInWithTOTP: PropTypes.func.isRequired
// });

export default AuthProvider;
